import { IBaseConfigModule } from 'library/ts/interfaces/config/IBaseConfigModule';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { IConfig } from 'tag/ts/tag/interfaces/config/IConfig';
import { IConfigParam } from 'tag/ts/tag/interfaces/config/IConfigParam';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Config processing utilities
 */
export class ConfigUtilities {

	/**
	 * @description Gets configs from params passed by page
	 * @param {string} uuid
	 * @param {IConfigParam} config
	 * @param {( config: IConfig ) => void} onSuccess
	 * @param {() => void} onFailure
	 */
	static getConfigs = ( uuid: string, config: IConfigParam, onSuccess: ( config: IConfig ) => void, onFailure: () => void ): void => {
		const utils = window.SmartTag.utilities;
		const logger = LogService.create( { prefixes: [ 'SmartTag:', 'Tag:', uuid ] } );

		// INFO: ===== ITERATE OVER CONFIGS ARRAY => START =====

		/**
		 * @description Requests data using fetch for valid URL or resolves without fetch for Object / JSON item
		 * @param configsArrayItem
		 * @return {Promise<IBaseConfigModule | void>}
		 */
		const requestData = ( configsArrayItem: string | Record<string, any> | any ): Promise<IBaseConfigModule | void> => {
			return new Promise(
				( resolve, reject ) => {

					// INFO: URL
					if ( utils.Type.isString( configsArrayItem ) && utils.URL.valid( configsArrayItem ) ) {
						// make fetch request
						fetch(
							configsArrayItem
						)
							.then(
								( response ) => {
									return response.text();
									// return response.json();
								}
							)
							.then(
								( data ) => {
									resolve( data ? JSON.parse( data ) : {} );
								}
							)
							.then(
								( data ) => {
									logger.log( 'getConfigs:', 'Fetch success:', data );
									resolve( data );
								}
							)
							.catch(
								( error ) => {
									logger.warn( 'getConfigs:', 'Fetch error:', error );
								}
							)
							.finally(
								() => {
									logger.log( 'getConfigs:', 'Fetch finally:', 'Resolve with empty object.' );
									resolve( {} as IBaseConfigModule );
								}
							);
					}

					// INFO: Object or JSON
					if ( utils.Type.isObject( configsArrayItem ) || utils.Type.isJSON( configsArrayItem ) ) {
						resolve( configsArrayItem );
					}
				}
			);
		};

		/**
		 * @description Processes config section configs array
		 * @return {Promise<( IBaseConfigModule )[]>}
		 * @param configsArray
		 */
		const processConfigsArray = ( configsArray: ( string | { [ key: string ]: IBaseConfigModule } )[] ): Promise<( IBaseConfigModule )[]> => {
			return configsArray.reduce(
				( promise, configsArrayItem, index ) => {
					return promise.then(
						( results ) => {
							return requestData( configsArrayItem ).then(
								( data ) => {
									results.push( data as IBaseConfigModule );
									return results;
								}
							);
						}
					);
				}, Promise.resolve( [] )
			);
		};

		// INFO: ===== ITERATE OVER CONFIGS ARRAY => END =====

		// INFO: ===== ITERATE OVER CONFIG SECTIONS => START =====

		/**
		 * @description Processes config section
		 * @param {string} section
		 * @param {any[]} sectionConfigsArray
		 * @return {Promise<{ section: string, configs: ( string | IBaseConfigModule )[] }>}
		 */
		const processConfigSection = ( section: string, sectionConfigsArray: any[] ): Promise<{ section: string, configs: ( string | IBaseConfigModule )[] }> => {
			// check if configs array is not undefined. if it is not an array turn it to array
			if ( utils.Type.isUndefined( sectionConfigsArray ) ) {
				sectionConfigsArray = [];
			} else {
				if ( !utils.Type.isArray( sectionConfigsArray ) ) {
					sectionConfigsArray = [ sectionConfigsArray ];
				}
			}

			return new Promise(
				( resolve ) => {
					logger.log(
						'\ngetConfigs:',
						'\nProcessing:',
						'\nSection:',
						section,
						'\nSection configs array:',
						sectionConfigsArray,
						'\Configs:',
						sectionConfigsArray.length,
						'\nStart iterating over section configs',
						'\n----------------'
					);

					processConfigsArray( sectionConfigsArray ).then(
						( configs ) => {
							resolve(
								{
									section: section,
									configs: configs
								}
							);
						}
					);
				}
			);
		};

		// IMPORTANT: Start execution
		Promise.all(
			Object.keys( config ).map(
				( section ) => {
					return processConfigSection( section, config[ section ] );
				}
			)
		).then(
			( result ) => { // results will be an array of the values resolved in the promises
				logger.log( 'getConfigs:', 'Done:\nResults:', result, '\nConfig:', config );

				// create instance of output config
				const outputConfig: IConfig = {};

				// assign sections to output config object so original params.config is not affected
				result.forEach(
					( item ) => {
						outputConfig[ item.section ] = item.configs.reduce( ( acc, obj ) => utils.DeepMerge.deepmerge( acc, obj, undefined ), {} );
					}
				);

				logger.log( 'getConfigs:', 'Config flattened:', outputConfig );

				if ( utils.Type.isFunction( onSuccess ) ) onSuccess( outputConfig );
			}
		);

		// INFO: ===== ITERATE OVER CONFIG SECTIONS => END =====
	};

}